import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

// ✅ Debounce utility function
const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

// ✅ Static style objects
const BASE_CARD_STYLES = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'white',
  willChange: 'transform, height',
  pointerEvents: 'auto',
};

const DRAG_HANDLE_BASE_STYLES = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 100
};

const HANDLE_BAR_BASE_STYLES = {
  backgroundColor: '#d1d5db',
  borderRadius: '9999px',
  transition: 'all 0.3s ease',
  pointerEvents: 'none'
};

const ICON_BASE_STYLES = {
  color: '#6b7280',
  pointerEvents: 'none'
};

const HINT_TEXT_BASE_STYLES = {
  color: '#9ca3af',
  fontWeight: '500',
  textAlign: 'center',
  transition: 'opacity 0.2s ease',
  pointerEvents: 'none'
};

const CONTENT_WRAPPER_BASE_STYLES = {
  overflow: 'hidden',
  position: 'relative'
};

// ✅ Calculate responsive heights with 80% max
const calculateResponsiveHeights = (screenWidth, screenHeight) => {
  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;
  
  // NEW: Detect if device is in portrait mode (vertical)
  const isPortrait = screenHeight > screenWidth;
  
  // NEW: Treat portrait mode on large screens (TVs) like tablets
  const isLargePortrait = screenWidth >= 1024 && isPortrait;

  if (isMobile) {
    return {
      initial: screenHeight * 0.25,
      max: screenHeight * 0.8,
      min: 40,
      closed: 35
    };
  } else if (isTablet || isLargePortrait) { // ADD isLargePortrait here
    return {
      initial: screenHeight * 0.2,
      max: screenHeight * 0.8,
      min: 50,
      closed: 40
    };
  } else {
    return {
      initial: screenHeight * 0.15,
      max: screenHeight * 0.8,
      min: 60,
      closed: 50
    };
  }
};

// ✅ Drag Handle Component
const DragHandle = memo(({ 
  isClosed, 
  isDragging, 
  height, 
  initialHeight,
  isMobile,
  handleRef
}) => {
  const handleBarStyles = useMemo(() => ({
    ...HANDLE_BAR_BASE_STYLES,
    width: isMobile ? '32px' : '28px',
    height: isMobile ? '4px' : '3px'
  }), [isMobile]);

  const iconSize = useMemo(() => 
    isMobile ? { height: '14px', width: '14px' } : { height: '12px', width: '12px' }
  , [isMobile]);

  const IconComponent = useMemo(() => {
    if (isClosed && !isDragging) return ChevronUp;
    if (height > initialHeight) return ChevronDown;
    return ChevronUp;
  }, [isClosed, isDragging, height, initialHeight]);

  const hintText = useMemo(() => {
    if (isClosed) {
      return isMobile ? 'Drag to open' : 'Drag to open';
    } else {
      return isMobile ? 'Drag to resize' : 'Drag to close • Drag to resize';
    }
  }, [isClosed, isMobile]);

  return (
    <div
      ref={handleRef}
      data-drawer-handle="true"
      style={{
        ...DRAG_HANDLE_BASE_STYLES,
        padding: isMobile ? '3px' : '8px',
        cursor: isDragging ? 'grabbing' : 'grab',
        borderBottom: !isClosed ? '1px solid #e5e7eb' : 'none',
        minHeight: isMobile ? '40px' : '35px',
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '6px',
        marginBottom: isMobile ? '3px' : '2px',
        pointerEvents: 'none'
      }}>
        <div style={handleBarStyles}></div>
        <IconComponent style={{ ...iconSize, ...ICON_BASE_STYLES }} />
      </div>

      <div style={{
        ...HINT_TEXT_BASE_STYLES,
        fontSize: isMobile ? '10px' : '9px',
        opacity: isDragging ? 0.6 : 1,
        paddingBottom: isMobile ? '2px' : '1px',
        maxWidth: '120px',
        textAlign: 'center',
        lineHeight: 1.2
      }}>
        {hintText}
      </div>
    </div>
  );
});

DragHandle.displayName = 'DragHandle';

// ✅ Content Area Component
const ContentArea = memo(({ 
  children, 
  isDragging, 
  isInteracting,
  handleHeight,
  isClosed
}) => {
  return (
    <div
      data-drawer-content="true"
      style={{
        ...CONTENT_WRAPPER_BASE_STYLES,
        height: `calc(100% - ${handleHeight}px)`,
        pointerEvents: isClosed ? 'none' : 'auto',
        opacity: isClosed ? 0 : 1,
        visibility: isClosed ? 'hidden' : 'visible',
        transition: 'opacity 0.2s ease, visibility 0.2s ease'
      }}
    >
      <div style={{ 
        height: '100%', 
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {children}
      </div>
    </div>
  );
});

ContentArea.displayName = 'ContentArea';

// ✅ Main DraggableCard Component
const DraggableCard = ({
  children,
  className = '',
  initialHeight = 200,
  maxHeight = 600,
  minHeight = 100,
  onHeightChange,
  onInteractionStart,
  externalHeight,
  onDrawerStateChange
}) => {
  const [screenSize, setScreenSize] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024
  }));

  // ✅ Store previous screen size to detect changes
  const prevScreenSizeRef = useRef(screenSize);

  const responsiveHeights = useMemo(() => 
    calculateResponsiveHeights(screenSize.width, screenSize.height)
  , [screenSize.width, screenSize.height]);

  const [height, setHeight] = useState(responsiveHeights.initial);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);

  const cardRef = useRef(null);
  const handleRef = useRef(null);
  const dragStateRef = useRef({ 
    isDragging: false, 
    startY: 0, 
    startHeight: 0
  });
  const interactionTimeoutRef = useRef(null);

  // ✅ CRITICAL: Aggressive height adjustment on screen size change
  useEffect(() => {
    const prevHeight = prevScreenSizeRef.current.height;
    const currentHeight = screenSize.height;
    
    // Detect if screen height changed significantly (rotation)
   if (Math.abs(prevHeight - currentHeight) > 20) {
      console.log('🔄 Screen rotation detected:', {
        from: `${prevHeight}px`,
        to: `${currentHeight}px`,
        diff: `${currentHeight - prevHeight}px`
      });
      
      // If drawer is open, maintain the same relative position
      if (!isClosed) {
        // Calculate what percentage of old screen the drawer occupied
        const oldPercentage = height / prevHeight;
        
        // Apply same percentage to new screen height
        let newHeight = oldPercentage * currentHeight;
        
        // But cap at 80% max
        newHeight = Math.min(newHeight, responsiveHeights.max);
        
        // And ensure it's at least the initial height
        newHeight = Math.max(newHeight, responsiveHeights.initial);
        
        console.log('📊 Adjusting height:', {
          oldHeight: `${height}px`,
          oldPercentage: `${(oldPercentage * 100).toFixed(1)}%`,
          newHeight: `${newHeight.toFixed(0)}px`,
          maxAllowed: `${responsiveHeights.max}px`
        });
        
        setHeight(newHeight);
      } else {
        // If closed, keep it closed
        setHeight(responsiveHeights.closed);
      }
    }
    
    // Update previous screen size
    prevScreenSizeRef.current = screenSize;
  }, [screenSize, responsiveHeights.max, responsiveHeights.initial, responsiveHeights.closed, height, isClosed]);

  // ✅ Update height from external control
  useEffect(() => {
    if (externalHeight !== undefined && externalHeight !== height) {
      const constrainedHeight = Math.max(
        responsiveHeights.min,
        Math.min(externalHeight, responsiveHeights.max)
      );
      setHeight(constrainedHeight);
      setIsClosed(constrainedHeight <= responsiveHeights.closed + 15);
    }
  }, [externalHeight, height, responsiveHeights.closed, responsiveHeights.min, responsiveHeights.max]);

  const cardStyles = useMemo(() => ({
    ...BASE_CARD_STYLES,
    borderTopLeftRadius: screenSize.isMobile ? '20px' : '16px',
    borderTopRightRadius: screenSize.isMobile ? '20px' : '16px',
    boxShadow: isClosed && !isDragging 
      ? '0 -2px 10px rgba(0, 0, 0, 0.1)' 
      : '0 -10px 25px rgba(0, 0, 0, 0.15)',
    transition: isDragging ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 30,
    height: `${height}px`,
    maxHeight: `${responsiveHeights.max}px`
  }), [screenSize.isMobile, isClosed, isDragging, height, responsiveHeights.max]);

  const handleHeight = useMemo(() => 
    screenSize.isMobile ? 40 : 35
  , [screenSize.isMobile]);

  const updateHeight = useCallback((newHeight) => {
    const validHeight = Math.max(
      responsiveHeights.min,
      Math.min(newHeight, responsiveHeights.max)
    );
    
    setHeight(validHeight);
    
    if (onHeightChange) {
      onHeightChange(validHeight);
    }

    const isOpen = validHeight > responsiveHeights.closed + 15;
    setIsClosed(!isOpen);

    if (onDrawerStateChange) {
      onDrawerStateChange(isOpen ? 'open' : 'closed');
    }
  }, [onHeightChange, onDrawerStateChange, responsiveHeights.closed, responsiveHeights.min, responsiveHeights.max]);

  // ✅ IMMEDIATE resize handler (no debounce for rotation)
  const handleResize = useCallback(() => {
    const newScreenSize = {
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth < 768,
      isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
      isDesktop: window.innerWidth >= 1024
    };
    
    console.log('📱 Resize event:', {
      from: `${screenSize.width}x${screenSize.height}`,
      to: `${newScreenSize.width}x${newScreenSize.height}`
    });
    
    setScreenSize(newScreenSize);
  }, [screenSize.width, screenSize.height]);

  // ✅ Debounced version for continuous resizing
  const debouncedResize = useMemo(
    () => debounce(handleResize, 150)
  , [handleResize]);

  // ✅ Handle resize and orientation change
  useEffect(() => {
    // Use immediate for orientationchange, debounced for resize
    const handleOrientationChange = () => {
      console.log('🔄 Orientation change detected');
      handleResize(); // Immediate, no debounce
    };

    window.addEventListener('resize', debouncedResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize, debouncedResize]);

  const handleTouchStart = useCallback((e) => {
    e.stopPropagation();
    setIsDragging(true);
    setIsInteracting(true);
    dragStateRef.current = { 
      isDragging: true, 
      startY: e.touches[0].clientY, 
      startHeight: height
    };
    if (onInteractionStart) {
      onInteractionStart();
    }
  }, [height, onInteractionStart]);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDragging(true);
    setIsInteracting(true);
    dragStateRef.current = { 
      isDragging: true, 
      startY: e.clientY, 
      startHeight: height
    };
    if (onInteractionStart) {
      onInteractionStart();
    }
  }, [height, onInteractionStart]);

  const handleMove = useCallback((clientY) => {
    if (!dragStateRef.current.isDragging) return;
    
    const deltaY = dragStateRef.current.startY - clientY;
    let newHeight;
    
    if (isClosed) {
      newHeight = Math.max(
        responsiveHeights.closed, 
        Math.min(responsiveHeights.max, responsiveHeights.closed + deltaY)
      );
    } else {
      newHeight = Math.max(
        responsiveHeights.closed, 
        Math.min(responsiveHeights.max, dragStateRef.current.startHeight + deltaY)
      );
    }
    
    updateHeight(newHeight);
  }, [isClosed, responsiveHeights, updateHeight]);

  const handleTouchMove = useCallback((e) => {
    if (dragStateRef.current.isDragging) {
      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();
    }
    handleMove(e.touches[0].clientY);
  }, [handleMove]);

  const handleMouseMove = useCallback((e) => {
    if (dragStateRef.current.isDragging) {
      e.preventDefault();
      e.stopPropagation();
    }
    handleMove(e.clientY);
  }, [handleMove]);

  const handleEnd = useCallback((e) => {
    if (e) {
      e.stopPropagation();
      if (e.cancelable) {
        e.preventDefault();
      }
    }

    setIsDragging(false);
    dragStateRef.current.isDragging = false;

    if (height <= responsiveHeights.closed + 15) {
      setIsClosed(true);
      updateHeight(responsiveHeights.closed);
    } else {
      setIsClosed(false);
    }
  }, [height, responsiveHeights, updateHeight]);

  useEffect(() => {
    const handleElement = handleRef.current;
    
    if (handleElement) {
      const touchStartHandler = (e) => {
        e.preventDefault();
        handleTouchStart(e);
      };

      const mouseDownHandler = (e) => {
        e.preventDefault();
        handleMouseDown(e);
      };

      handleElement.addEventListener('touchstart', touchStartHandler, { passive: false });
      handleElement.addEventListener('mousedown', mouseDownHandler, { passive: false });

      return () => {
        handleElement.removeEventListener('touchstart', touchStartHandler);
        handleElement.removeEventListener('mousedown', mouseDownHandler);
      };
    }
  }, [handleTouchStart, handleMouseDown]);

  useEffect(() => {
    if (isDragging) {
      const options = { passive: false, capture: true };
      
      document.addEventListener('touchmove', handleTouchMove, options);
      document.addEventListener('mousemove', handleMouseMove, options);
      document.addEventListener('touchend', handleEnd, options);
      document.addEventListener('mouseup', handleEnd, options);

      return () => {
        document.removeEventListener('touchmove', handleTouchMove, options);
        document.removeEventListener('mousemove', handleMouseMove, options);
        document.removeEventListener('touchend', handleEnd, options);
        document.removeEventListener('mouseup', handleEnd, options);
      };
    }
  }, [isDragging, handleTouchMove, handleMouseMove, handleEnd]);

  useEffect(() => {
    if (onHeightChange) {
      onHeightChange(height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      data-drawer="true"
      style={cardStyles}
      className={className}
    >
      <DragHandle
        handleRef={handleRef}
        isClosed={isClosed}
        isDragging={isDragging}
        height={height}
        initialHeight={responsiveHeights.initial}
        isMobile={screenSize.isMobile}
      />

      <ContentArea
        isDragging={isDragging}
        isInteracting={isInteracting}
        handleHeight={handleHeight}
        isClosed={isClosed}
      >
        {children}
      </ContentArea>
    </div>
  );
};

export default memo(DraggableCard);
